/**
 * Splits "Moje rezervácie" into upcoming and past by when a booking ends, not by its date:
 * an hour that finished this morning belongs to history, not to "Nadchádzajúce" until midnight.
 * A multi-hour booking moves as a whole once its last hour is over, so it is never split
 * between the two lists.
 */

export interface SplittableBooking {
  booking_group_id: string;
  slots?: { date: string; end_time: string } | null;
}

/** "2026-10-24" + "12:00:00" → "2026-10-24 12:00", comparable as a string. */
const endOf = (b: SplittableBooking) => `${b.slots!.date} ${b.slots!.end_time.slice(0, 5)}`;

/**
 * `now` is the local wall clock as "YYYY-MM-DD HH:MM" (the customer and the studio share
 * Bratislava time, like the rest of the widget). Bookings without slot data are dropped.
 */
export function splitByGroupEnd<T extends SplittableBooking>(bookings: T[], now: string): { upcoming: T[]; past: T[] } {
  const withSlots = bookings.filter((b) => b.slots);
  const groupEnd = new Map<string, string>();
  for (const b of withSlots) {
    const key = b.booking_group_id || endOf(b);
    const end = endOf(b);
    if (!groupEnd.has(key) || end > groupEnd.get(key)!) groupEnd.set(key, end);
  }
  const upcoming: T[] = [];
  const past: T[] = [];
  for (const b of withSlots) {
    const end = b.booking_group_id ? groupEnd.get(b.booking_group_id)! : endOf(b);
    (end > now ? upcoming : past).push(b);
  }
  return { upcoming, past };
}

/** The local wall clock in the format splitByGroupEnd expects. */
export function localNow(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
